import React from 'react';
import { useTranslation } from 'react-i18next';

import { Icon, ScoreGauge, Skeleton } from '../../../../components';
import { useMountAnimation } from '../../../../hooks/useMountAnimation';
import { ACQ, heroGlowForScore } from '../../../../theme/tokens';
import {
  Root,
  Score,
  Heading,
  Actions,
  ActionDivider,
  Action,
  ActionLeft,
  Spark,
  ActionLabel,
  Chev,
} from './Hero.styles';

const LUMIO_SPARK = '/assets/acq/lumio-spark.svg';

// The gauge's default ring diameter (ScoreGauge `size`), reused so the loading
// placeholder reserves the same footprint.
const GAUGE_SIZE = 124;

export type HeroAction = { id: string; label: string };

export type HeroProps = {
  score: number;
  status: string;
  /** 0-100. Fraction of the ring the gauge fills to. */
  progress: number;
  heading: string;
  actions: HeroAction[];
  /** The acted action id is read from `event.currentTarget.dataset.id`. */
  onAction: (event: React.MouseEvent<HTMLElement>) => void;
  /** While true, the gauge + heading show placeholders (the insights endpoint is loading). */
  loading?: boolean;
};

/**
 * The gauge, mounted only once the score is in so its draw-in (stroke-dashoffset)
 * plays on reveal — it owns its own `useMountAnimation` rather than reading a
 * page-level flag that has already flipped by the time the data lands.
 */
const HeroGauge = ({ score, status, progress }: { score: number; status: string; progress: number }): React.ReactElement => {
  const mounted = useMountAnimation();
  return <ScoreGauge score={score} status={status} progress={progress} animate={mounted} size={GAUGE_SIZE} />;
};

/**
 * Hero status banner — the shared (operations) score gauge, colour-graded by its
 * value, plus the headline and the two AI action buttons.
 */
export const Hero = ({ score, status, progress, heading, actions, onAction, loading = false }: HeroProps): React.ReactElement => {
  const { t } = useTranslation();
  // The headline + action labels are a fixed, state-driven UI vocabulary that
  // reaches this section as dynamic props (derived from the score). Translate them
  // at the render site over the known set so the literals are harvested and the
  // copy re-translates on a live language switch; an unknown value renders as-is.
  const headingText = (value: string): string => {
    const labels: Record<string, string> = {
      'Player Acquisition is on track': t('Player Acquisition is on track'),
      'Player Acquisition is doing well': t('Player Acquisition is doing well'),
      'Player Acquisition needs attention': t('Player Acquisition needs attention'),
      'Player Acquisition needs budget and campaigns': t('Player Acquisition needs budget and campaigns'),
      'Player Acquisition is low on budget': t('Player Acquisition is low on budget'),
      'Player Acquisition has campaigns to activate': t('Player Acquisition has campaigns to activate'),
      // The affiliate score's next steps (outside the "777" reveal, see `affiliate-score.ts`).
      'Set up your affiliate program': t('Set up your affiliate program'),
      'Invite your first affiliate': t('Invite your first affiliate'),
      'Create an affiliate campaign': t('Create an affiliate campaign'),
      'Bring in players through affiliates': t('Bring in players through affiliates'),
      'Turn affiliate players into depositors': t('Turn affiliate players into depositors'),
    };
    return labels[value] ?? value;
  };
  const actionLabel = (label: string): string => {
    const labels: Record<string, string> = {
      'Increase daily budget': t('Increase daily budget'),
      'Activate a new campaign': t('Activate a new campaign'),
      'Open Affiliates': t('Open Affiliates'),
    };
    return labels[label] ?? label;
  };
  return (
    <Root $glow={heroGlowForScore(score)}>
      {/* With no actions panel beside it, let the gauge + heading span the card so
          the (short) headline flows naturally instead of wrapping in a fixed box. */}
      <Score $expand={actions.length === 0}>
        {loading ? (
          <Skeleton circle width={GAUGE_SIZE} height={GAUGE_SIZE} />
        ) : (
          <HeroGauge score={score} status={status} progress={progress} />
        )}
        {loading ? <Skeleton width={200} height={18} radius={6} /> : <Heading text={headingText(heading)} tag="span" />}
      </Score>

      {/* Suggestions only when a score half is lacking; when both are healthy the
          block is omitted entirely — the state-aware heading already tells the
          owner acquisition is on track. */}
      {actions.length > 0 ? (
        <Actions>
          {actions.map((action, index) => (
            <React.Fragment key={action.id}>
              {index > 0 ? <ActionDivider /> : null}
              <Action type="button" data-id={action.id} onClick={onAction}>
                <ActionLeft>
                  <Spark src={LUMIO_SPARK} alt="" width={16} height={16} />
                  <ActionLabel text={actionLabel(action.label)} tag="span" />
                </ActionLeft>
                <Chev>
                  <Icon name="arrow-right-circle" size={24} color={ACQ.t50} />
                </Chev>
              </Action>
            </React.Fragment>
          ))}
        </Actions>
      ) : null}
    </Root>
  );
};

export default Hero;
